import React, { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Box, Typography, IconButton, Divider, MenuItem, Checkbox } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import zeplinColor from '../../../../theme/Color';
import style from './CalendarPanel.module.css';
import { ReactComponent as SlackUser } from '../../../../assets/slack-user.svg';
import CustomRangePicker from '../../../shared/CustomRangePicker/CustomRangePicker';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomSearch from '../../../shared/CustomSearch/CustomSearch';
import { ReactComponent as VerticalDots } from '../../../../assets/vertical-dots.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/close-red.svg';
import { ReactComponent as ViewIcon } from '../../../../assets/view.svg';
import CustomPopOver from '../../../shared/CustomPopOver/CustomPopOver';
import { Link, useLocation } from 'react-router-dom';
import { AuthService } from '../../../../service/auth.service';
import { SlackService } from '../../../../service/slack.service';
import CustomAlert from '../../../shared/CustomAlert/CustomAlert';
import CustomAlertContain from '../../../shared/CustomAlert/CustomAlertContain';
import CustomCard from '../../../shared/CustomCard/CustomCard';
import CustomModal from '../../../shared/CustomModal/CustomModal';

interface CalendarPanelProps {
  onDateChange: (date: Dayjs | null) => void;
  currentMonth: number;
  currentYear: number;
  onMonthYearChange?: (month: number, year: number) => void; //  Add callback for month/year changes
  currentWeekDate?: Dayjs;
  collapsed?: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const CalendarPanel: React.FC<CalendarPanelProps> = ({
  onDateChange,
  currentMonth,
  currentYear,
  onMonthYearChange, //  Add the prop
  currentWeekDate,
  collapsed = false,
  setCollapsed,
}) => {
  const location = useLocation();
  const isGoogleConnected = sessionStorage.getItem('isGoogleConnected') === 'true';
  const isZoomConnected = sessionStorage.getItem('isZoomConnected') === 'true';
  const isCalendarConnecting = sessionStorage.getItem('calendar-connecting') === 'true';
  // const isZoomConnected = false;
  const isMSTeamsConnected = sessionStorage.getItem('MSToken') === 'true';
  const Email = sessionStorage.getItem('current-user') || '';
  const msToken = sessionStorage.getItem('MSToken') || '';
  const [success, setSuccess] = useState(false);
  const [alertType, setAlertType] = useState('');
  const [selectedItems, setSelectedItems] = useState({
    tools: [
      isGoogleConnected ? 'Google Meet' : '',
      isZoomConnected ? 'Zoom' : '',
      isMSTeamsConnected ? 'Microsoft Teams' : '',
    ],
  });
  const [taskAnchorEl, setTaskAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(
    isZoomConnected || isGoogleConnected || isMSTeamsConnected ? false : true,
  );
  const [internalDisplayDate, setInternalDisplayDate] = useState<Dayjs>(
    currentWeekDate || dayjs().year(currentYear).month(currentMonth),
  );
  const [calendarProvider, setCalendarProvider] = useState(
    sessionStorage.getItem('calendar-provider') || '',
  );
  const [optionAnchorEl, setOptionAnchorEl] = React.useState<HTMLImageElement | null>(null);
  const [loading, setLoading] = useState(false);
  // const [events, setEvents] = useState<any[]>([]);
  const openTask = Boolean(taskAnchorEl);
  const idTask = openTask ? 'simple-popover' : undefined;
  const openOption = Boolean(optionAnchorEl);
  const idOption = openOption ? 'simple-popover' : undefined;

  //  Create current date from passed month/year
  const currentDate = currentWeekDate || dayjs().year(currentYear).month(currentMonth);

  console.log('CalendarPanel: Rendered with', { currentMonth, currentYear, currentDate });
  //  Handle month navigation
  const handlePreviousMonth = () => {
    let newMonth = currentMonth;
    let newYear = currentYear;

    if (currentMonth === 0) {
      newMonth = 11;
      newYear = currentYear - 1;
    } else {
      newMonth = currentMonth - 1;
    }

    // Notify parent component about the change
    if (onMonthYearChange) {
      onMonthYearChange(newMonth, newYear);
    }
  };

  const handleNextMonth = () => {
    let newMonth = currentMonth;
    let newYear = currentYear;

    if (currentMonth === 11) {
      newMonth = 0;
      newYear = currentYear + 1;
    } else {
      newMonth = currentMonth + 1;
    }
    console.log('CalendarPanel: Navigating to', { newMonth, newYear });
    // Notify parent component about the change
    if (onMonthYearChange) {
      onMonthYearChange(newMonth, newYear);
    }
  };

  //  Handle date changes from calendar
  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      // If the selected date is in a different month/year, update those too
      if (date.month() !== currentMonth || date.year() !== currentYear) {
        if (onMonthYearChange) {
          onMonthYearChange(date.month(), date.year());
        }
      }
    }
    onDateChange(date);
  };

  const fetchToken = async (code: string = '', isExistingAccessTokenValid: boolean = false) => {
    try {
      setLoading(true);
      const res =
        calendarProvider === 'Microsoft Teams' || isExistingAccessTokenValid
          ? await AuthService.ms_team_events(code, Email, isExistingAccessTokenValid)
          : calendarProvider === 'Zoom'
          ? await AuthService.zoom_auth(code, process.env.REACT_APP_REDIRECT || '')
          : await AuthService.google_auth(code, process.env.REACT_APP_REDIRECT || '');

      sessionStorage.setItem(
        calendarProvider === 'Microsoft Teams' || isExistingAccessTokenValid
          ? 'MSToken'
          : calendarProvider === 'Zoom'
          ? 'isZoomConnected'
          : 'isGoogleConnected',
        'true',
      );
      sessionStorage.removeItem('calendar-provider');
      setSelectedItems((prev: any) => ({
        ...prev,
        tools: [...prev.tools, calendarProvider],
      }));
      window.dispatchEvent(
        new CustomEvent('eventConnected', {
          detail: {
            key:
              calendarProvider === 'Microsoft Teams' || isExistingAccessTokenValid
                ? 'MSToken'
                : calendarProvider === 'Google Meet'
                ? 'isGoogleConnected'
                : calendarProvider === 'Zoom'
                ? 'isZoomConnected'
                : '',
            value: 'true',
            data: res,
          },
        }),
      );
      console.log(res, 'response after fetching token');
      if (res) {
        sessionStorage.setItem('meetings', JSON.stringify(res));
        // setEvents(res.meetings);
        setSuccess(true);
        setAlertType(`${calendarProvider} connected`);
      }
    } catch (err: any) {
      // setEvents((prev) => prev);
      console.log(err, 'error in fetching token');
    } finally {
      setLoading(false);
      sessionStorage.setItem('calendar-connecting', 'false');
    }
  };

  useEffect(() => {
    if (location?.search?.split('code=')[1]) {
      fetchToken(location?.search?.split('code=')[1]);
    }
  }, [location?.search?.split('code=')[1]]);

  useEffect(() => {
    if (msToken && msToken === 'true') {
      fetchToken('', true);
    }
  }, [msToken]);
  const handleClick = (event: React.MouseEvent<HTMLImageElement>) => {
    event.stopPropagation();
    setSuccess(false);
    setOptionAnchorEl(event.currentTarget);
  };
  const handleRefresh = async () => {
    try {
      const res = await AuthService.google_refresh();
      console.log(res);
    } catch (err) {
      console.log(err);
    }
  };
  const handleUnlink = async (provider: string) => {
    try {
      setLoading(true);
      const res = await SlackService.unlinkSlack({
        ...(calendarProvider === 'Google Meet'
          ? { unlinkGoogle: true }
          : calendarProvider === 'Zoom'
          ? { unlinkZoom: true }
          : { unlinkMS: true }),
      }); // Assuming you use the same service or create a new one
      sessionStorage.setItem(
        calendarProvider === 'Google Meet'
          ? 'isGoogleConnected'
          : calendarProvider === 'Zoom'
          ? 'isZoomConnected'
          : 'MSToken',
        'false',
      );
      calendarProvider === 'Microsoft Teams' && sessionStorage.removeItem('meetings');
      setOptionAnchorEl(null);
      setModalOpen(false);
      setSelectedItems((prev: any) => ({
        ...prev,
        tools: prev.tools.filter((tool: string) => tool !== provider),
      }));
      window.dispatchEvent(
        new CustomEvent('eventConnected', {
          detail: {
            key:
              calendarProvider === 'Google Meet'
                ? 'isGoogleConnected'
                : calendarProvider === 'Zoom'
                ? 'isZoomConnected'
                : 'MSToken',
            value: 'false',
          },
        }),
      );
      setSuccess(true);
      setAlertType(`${calendarProvider} disconnected`);
      setCalendarProvider('');
      // setEvents((prev) => prev.filter((event) => event.provider !== provider));
      // window.history.replaceState({}, document.title, window.location.pathname);
      // window.location.reload();
    } catch (err) {
      console.error('Error unlinking Google:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleClose = () => {
    setModalOpen(false);
  };
  return collapsed ? (
    <Box
      sx={{
        padding: '5px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: '50%',
        '&:hover': { backgroundColor: zeplinColor.Neutral4 },
      }}
      onClick={() => setCollapsed((prev) => !prev)}
    >
      <img src={'/assets/collapse-icon.svg'} />
    </Box>
  ) : (
    <Box
      sx={{
        height: { xs: 'calc(100vh - 300px)', xl: 'calc(100vh - 360px)' },
        border: `1px solid ${zeplinColor.Neutral5}`,
        borderTopLeftRadius: '14px',
        borderTopRightRadius: '14px',
        overflowY: 'scroll',
        // transform: collapsed ? 'translateX(-100px)' : 'translateX(0px)',
        // transition: 'transform 2s ease-in-out;',
      }}
    >
      <Box
        sx={{
          padding: { xs: '16px', xl: '24px' },
        }}
      >
        {/*  Pass navigation handlers to CustomRangePicker */}
        <CustomRangePicker
          onDateChange={handleDateChange}
          currentDateSelected={currentDate}
          onPreviousMonth={handlePreviousMonth} //  Pass previous month handler
          onNextMonth={handleNextMonth} //  Pass next month handler
          setCollapsed={setCollapsed}
          internalDisplayDate={internalDisplayDate}
          setInternalDisplayDate={setInternalDisplayDate}
        />
      </Box>

      <Divider sx={{ borderColor: zeplinColor.Neutral5 }} />

      <Box
        sx={{
          padding: { xs: '16px', xl: '24px' },
        }}
      >
        <Typography variant='responsiveHeading4'>My Calendars</Typography>
        {['Zoom', 'Google Meet', 'Microsoft Teams'].map((name) => (
          <Box
            key={name}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 0',
              cursor: 'pointer',
              // '&:hover': {
              //   backgroundColor: zeplinColor.Neutral3,
              //   borderRadius: '8px',
              // },
            }}
            onClick={
              (name === 'Zoom' && isZoomConnected) ||
              (name === 'Google Meet' && isGoogleConnected) ||
              (name === 'Microsoft Teams' && isMSTeamsConnected)
                ? () => {
                    setSelectedItems((prev: any) =>
                      selectedItems.tools.indexOf(name) > -1
                        ? { ...prev, tools: prev.tools.filter((tool: string) => tool !== name) }
                        : {
                            ...prev,
                            tools: [...prev.tools, name],
                          },
                    );
                    window.dispatchEvent(
                      new CustomEvent('eventSelected', {
                        detail: {
                          key:
                            name === 'Google Meet'
                              ? 'Google Meet'
                              : name === 'Microsoft Teams'
                              ? 'MSTeams'
                              : 'Zoom',
                          //selectedItems current state is not immediately updated, so referring to selectedItems to get current state
                          value: selectedItems.tools.indexOf(name) > -1 ? 'false' : 'true',
                        },
                      }),
                    );
                  }
                : () => {}
            }
          >
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <Checkbox
                disabled={
                  !(
                    (name === 'Zoom' && isZoomConnected) ||
                    (name === 'Google Meet' && isGoogleConnected) ||
                    (name === 'Microsoft Teams' && isMSTeamsConnected)
                  )
                }
                checked={selectedItems.tools.indexOf(name) > -1}
                sx={{
                  color:
                    (name === 'Zoom' && isZoomConnected) ||
                    (name === 'Google Meet' && isGoogleConnected) ||
                    (name === 'Microsoft Teams' && isMSTeamsConnected)
                      ? zeplinColor.BasicBlack
                      : zeplinColor.Neutral5,
                  borderRadius: '4px',
                  padding: '0 8px 0 0',
                  '&.Mui-checked': {
                    color: zeplinColor.BasicBlack,
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: '14px', xl: '16px' },
                  color: zeplinColor.BasicBlack,
                }}
              >
                {name}
              </Typography>
            </Box>
            {(name === 'Zoom' && !isZoomConnected) ||
            (name === 'Google Meet' && !isGoogleConnected) ||
            (name === 'Microsoft Teams' && !isMSTeamsConnected) ? (
              <Link
                to={
                  name.toLowerCase() === 'google meet'
                    ? `https://accounts.google.com/o/oauth2/v2/auth/oauthchooseaccount?access_type=offline&prompt=consent&scope=https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fcalendar%20https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fcalendar.readonly%20https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fuserinfo.email&response_type=code&client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_REDIRECT}&service=lso&o2v=2&flowName=GeneralOAuthFlow`
                    : name.toLowerCase() === 'microsoft teams'
                    ? `https://login.microsoftonline.com/dbbc81d8-5d27-4c7d-86be-a95f37ae7601/oauth2/v2.0/authorize?client_id=de5103b1-fa13-4bd4-bf80-3630c23c345c&response_type=code&redirect_uri=${process.env.REACT_APP_REDIRECT}&scope=https://graph.microsoft.com/.default`
                    : `https://marketplace.zoom.us/authorize?client_id=vl_uXEAR3qovKQ11JzuNw&response_type=code&redirect_uri=${process.env.REACT_APP_REDIRECT}&_zmp_login_state=8A5xmIR2SUqZLoLVpS9pdw`
                }
                style={{ textDecoration: 'none' }}
                onClick={() => {
                  sessionStorage.setItem('calendar-provider', name);
                  sessionStorage.setItem('calendar-connecting', 'true');
                  // setTaskAnchorEl(null);
                }}
              >
                <CustomButton
                  disabled={false}
                  variant='outlined'
                  color='customSecondary'
                  style={{
                    color: zeplinColor.BasicBlack,
                    borderRadius: '8px',
                    height: '32px',
                    borderWidth: 1,
                    borderColor: zeplinColor.BasicBlack,
                    '&:hover': {
                      backgroundColor: zeplinColor.Neutral3,
                      color: zeplinColor.BasicBlack,
                    },
                    '&.MuiButton-root': {
                      padding: '6px 16px',
                    },
                  }}
                  textStyle={{ fontSize: '14px' }}
                  text={'Connect'}
                  icon={''}
                  onClick={(e: any) => {
                    // setTaskAnchorEl(e.currentTarget);
                    // setCalendarProvider(name.toLowerCase());
                  }}
                />
              </Link>
            ) : (
              <Box
                sx={{
                  padding: '5px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',

                  borderRadius: '50%',
                  '&:hover': { backgroundColor: zeplinColor.Neutral4 },
                }}
              >
                <VerticalDots
                  className={style['vertical-icon']}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    handleClick(e);
                    setCalendarProvider(name);
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Divider sx={{ borderColor: zeplinColor.Neutral5 }} />

      <CustomPopOver
        onClick={(e: any) => {
          e.stopPropagation();
        }}
        setAnchorEl={setOptionAnchorEl}
        anchorEl={optionAnchorEl}
        id={idOption}
        sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '16px',
          }}
        >
          <a
            href={
              calendarProvider === 'Google Meet'
                ? 'https://calendar.google.com'
                : calendarProvider === 'Microsoft Teams'
                ? 'https://www.microsoft.com/en/microsoft-teams/log-in'
                : ''
            }
            onClick={(e) => {
              e.stopPropagation();
              // handleCopyLink(data?.link);
            }}
            target='_blank'
            rel='noopener noreferrer'
            style={{ textDecoration: 'none' }}
          >
            <Box
              sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                // handleCopyLink(data?.link);
              }}
            >
              {/* <img src={'/assets/copy-link.svg'} width={18} height={18} /> */}
              <ViewIcon width={18} height={18} />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Open{' '}
                {calendarProvider === 'Google Meet'
                  ? 'Google Calendar'
                  : calendarProvider === 'Zoom'
                  ? 'Zoom'
                  : 'Microsoft Teams'}
              </Typography>
            </Box>
          </a>
          {/* <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleRefresh();
              // handleCopyLink(data?.link);
            }}
          >
            
            <img
              src={'/assets/refresh.svg'}
              alt='refresh-activity'
              id='refresh-activity'
              width={18}
              height={18}
            />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Refresh
            </Typography>
          </Box> */}
          <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              // handleCopyLink(data?.link);
              // handleUnlink(calendarProvider);
              setOptionAnchorEl(null);
              setModalOpen(true);
            }}
          >
            {/* <img src={'/assets/copy-link.svg'} width={18} height={18} /> */}
            <CloseIcon width={18} height={18} />
            <Typography variant='responsiveBody3' color={zeplinColor.Error}>
              {/* {loading ? 'Disconnecting...' : 'Disconnect'} */}
              Disconnect
            </Typography>
          </Box>
        </Box>
      </CustomPopOver>

      {showConnectModal && (
        <Box
          sx={{
            position: 'absolute',
            top: '70%',
            left: '40%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            // width: '90%',
            // maxWidth: '400px',

            // boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.2)',
          }}
          onClick={(e) => e.stopPropagation()} // ✅ Prevent event bubbling
        >
          <Box
            sx={{
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              backgroundColor: zeplinColor.BasicBlack,
              borderRadius: '8px',
              gap: '16px',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <img
                src={'assets/slack-connected.svg'}
                width={'40px'}
                height={'40px'}
                alt='connect meet'
              />
              <img
                src={'/assets/cross-white.svg'}
                alt='logo'
                width={'24px'}
                height={'24px'}
                onClick={() => setShowConnectModal(false)}
                style={{ cursor: 'pointer' }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',

                gap: '9px',
              }}
            >
              <Typography variant='responsiveHeading3' color={zeplinColor.Neutral1}>
                Connect Calendar
              </Typography>
              <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                Connect your preferred calendar (Zoom, Google Meet, Microsoft
                <br /> Teams, etc.) to view and manage upcoming meetings.
              </Typography>
            </Box>
            <CustomButton
              disabled={false}
              variant='outlined'
              color='customSecondary'
              style={{
                color: zeplinColor.Neutral1,
                borderRadius: '8px',
                borderWidth: 1,
                borderColor: zeplinColor.BasicBlack,
                backgroundColor: zeplinColor.Brand1,
                width: 'fit-content',
                '&:hover': {
                  backgroundColor: zeplinColor.Brand1,
                  color: zeplinColor.Neutral1,
                },
                height: '32px',
                '&.MuiButton-root': {
                  padding: '6px 16px',
                },
              }}
              textStyle={{ fontSize: '14px', lineHeight: '100%' }}
              text={'Okay'}
              icon={''}
              onClick={(e: any) => {
                // setTaskAnchorEl(e.currentTarget);
                setShowConnectModal(false);
              }}
            />
          </Box>
        </Box>
      )}

      <CustomAlert
        open={success && calendarProvider ? true : false}
        handleClose={() => {
          setSuccess(false);
          setAlertType('');
        }}
        message={
          <CustomAlertContain
            alertIcon={<img src='/assets/checkmark-green.png' width={24} height={24} />}
            alert={success ? `${alertType} successfully` : ''}
          />
        }
        error={false}
      />

      <CustomModal handleClose={handleClose} open={modalOpen}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: { xs: '20px', xl: '24px' },
              backgroundColor: zeplinColor.Neutral1,

              width: '375px',
              // height: '201px',
              boxShadow: 'rgba(0, 0, 0, 0.15)',
              // display: 'flex',
              // flexDirection: 'column',
              // justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '22px', xl: '32px' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                  Disconnect Calendar?
                </Typography>
                <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
                  {`Are you sure you want to disconnect your ${calendarProvider} calendar? You won’t be able to sync or view events until you reconnect.`}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',

                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                <Box
                  onClick={handleClose}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Neutral1,
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      cursor: 'pointer',
                      // width: '312px',
                      // height: '183px',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                      // display: 'flex',
                      // flexDirection: 'column',
                      // justifyContent: 'space-between',
                    }}
                    aria-role='button'
                    aria-disable={loading}
                  >
                    <Typography variant='buttonMedium' color={zeplinColor.BasicBlack}>
                      Cancel
                    </Typography>
                  </CustomCard>
                </Box>
                <Box
                  onClick={() =>
                    // handleCopyLink(data?.link);
                    handleUnlink(calendarProvider)
                  }
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Error,
                      cursor: 'pointer',
                      // border: `1px solid ${zeplinColor.BasicBlack}`,
                      // width: '312px',
                      // height: '183px',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                      // display: 'flex',
                      // flexDirection: 'column',
                      // justifyContent: 'space-between',
                    }}
                    aria-role='button'
                    aria-disable={loading}
                  >
                    <Typography
                      variant='buttonMedium'
                      color={zeplinColor.Neutral1}
                      sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    >
                      {loading ? 'Disconnecting...' : 'Disconnect'}
                    </Typography>
                  </CustomCard>
                </Box>
              </Box>
            </Box>
          </CustomCard>
          {/* {loading ? <CustomLoader /> : ''} */}
        </Box>
      </CustomModal>
    </Box>
  );
};

export default CalendarPanel;
